/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { Klass, LexicalNode, LexicalNodeReplacement } from 'lexical';

import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { HashtagNode } from '@lexical/hashtag';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { HorizontalRuleNode } from '@lexical/extension';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';

import { CollapsibleContainerNode } from '../plugins/CollapsiblePlugin/CollapsibleContainerNode';
import { CollapsibleContentNode } from '../plugins/CollapsiblePlugin/CollapsibleContentNode';
import { CollapsibleTitleNode } from '../plugins/CollapsiblePlugin/CollapsibleTitleNode';
import { AutocompleteNode } from './AutocompleteNode';
import { EmojiNode } from './EmojiNode';
import { EquationNode } from './EquationNode';
import { ExcalidrawNode } from './ExcalidrawNode';
import { FigmaNode } from './FigmaNode';
import { ImageNode } from './ImageNode/ImageNode';
import { KeywordNode } from './KeywordNode';
import { LayoutContainerNode } from './LayoutContainerNode';
import { LayoutItemNode } from './LayoutItemNode';
import { MentionNode } from './MentionNode';
import { PageBreakNode } from './PageBreakNode';
import { PollNode } from './PollNode';
import { StickyNode } from './StickyNode';
import { TweetNode } from './Twitter/TweetNode';
import { YouTubeNode } from './YouTube/YouTubeNode';
import { VideoNode } from './VideoNode';
import { AudioNode } from './AudioNode';
import { AdNode } from './AdNode';
import { CommentableParagraphNode } from './CommentableParagraphNode';
import { CommentableHeadingNode } from './CommentableHeadingNode';
import { CommentableListNode } from './CommentableListNode';
import { createBeautifulMentionNode } from 'lexical-beautiful-mentions';
import MentionComponent from './MentionNode/index';
import { PerspectivesNodes } from '@twygs/shared/lexical-editor';

const TwygsEditorNodes: Array<Klass<LexicalNode> | LexicalNodeReplacement> = [
  HeadingNode,
  ListNode,
  ListItemNode,
  QuoteNode,
  CodeNode,
  TableNode,
  TableCellNode,
  TableRowNode,
  HashtagNode,
  CodeHighlightNode,
  AutoLinkNode,
  AdNode,
  LinkNode,
  CommentableParagraphNode,
  CommentableHeadingNode,
  CommentableListNode,
  OverflowNode,
  PollNode,
  StickyNode,
  ImageNode,
  VideoNode,
  AudioNode,
  MentionNode,
  EmojiNode,
  ExcalidrawNode,
  EquationNode,
  AutocompleteNode,
  KeywordNode,
  HorizontalRuleNode,
  TweetNode,
  YouTubeNode,
  FigmaNode,
  MarkNode,
  CollapsibleContainerNode,
  CollapsibleContentNode,
  CollapsibleTitleNode,
  PageBreakNode,
  LayoutContainerNode,
  LayoutItemNode,
  // Perspectives custom nodes
  ...PerspectivesNodes,
  // Type assertion needed: lexical-beautiful-mentions uses different lexical version internally
  ...(createBeautifulMentionNode(MentionComponent) as unknown as Array<
    Klass<LexicalNode> | LexicalNodeReplacement
  >)
];

export default TwygsEditorNodes;
